import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { api, formatApiError } from "@/lib/api";
import { Loader2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message);
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-950 tmf-grain flex flex-col">
      <div className="p-6"><button onClick={() => navigate("/login")} className="text-emerald-200 flex items-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Login</button></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl tmf-rise">
          <Logo showTagline />
          <h1 className="mt-6 font-display font-bold text-2xl text-emerald-950">Set a new password</h1>
          {!token ? (
            <p className="mt-2 text-sm text-red-600" data-testid="reset-no-token">This reset link is invalid. Please request a new one from the Forgot Password page.</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>New Password</Label>
                <PasswordInput testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-12" />
                <p className="mt-1 text-[11px] text-gray-500">At least 8 characters.</p>
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <PasswordInput testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 h-12" />
              </div>
              <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reset Password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
